import React, { useState } from 'react';
import { HelpCircle, BookOpen, Search, ChevronDown, ChevronUp, FileText } from 'lucide-react';

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedGuides, setExpandedGuides] = useState(new Set());

  const guides = [
    {
      id: 'getting-started',
      category: 'Getting Started',
      color: '#3b82f6',
      title: 'Getting Started with Visaasure CRM',
      description: 'Learn the basics of navigating and using the CRM',
      steps: [
        {
          title: 'Dashboard Overview',
          content: 'The dashboard is your command center. It displays key metrics including total leads, active services, published testimonials, and active clients. Quick action buttons let you jump to common tasks.',
        },
        {
          title: 'Navigation Sidebar',
          content: 'The left sidebar contains all menu items. Access Leads, Clients, Agreements, and configuration pages. Your role determines which menu items are visible.',
        },
        {
          title: 'User Roles',
          content: 'The system has 4 roles: Super Admin (full access), Admin (all features except user management), Sales (lead management), and Process Consultant (client service delivery).',
        },
        {
          title: 'Search & Filters',
          content: 'Most pages include search bars and filters. Use stage, temperature, and source filters to find leads quickly. Apply multiple filters simultaneously for precise results.',
        }
      ]
    },
    {
      id: 'lead-management',
      category: 'Lead Management',
      color: '#10b981',
      title: 'Managing Leads',
      description: 'Complete guide to lead lifecycle management',
      steps: [
        {
          title: 'Understanding Lead Stages',
          content: 'Leads move through 12 stages: New → Contacted → Qualified → Consultation Scheduled → Consultation Completed → Proposal Sent → Agreement Sent → Agreement Signed → Payment Partial → Payment Full (WON) → Lost/Nurture.',
        },
        {
          title: 'Creating a Lead',
          content: 'Click "+ Create Lead" button. Fill in name, email, phone, service type, and destination country. Leads from the website are automatically created. Set temperature (Cold/Warm/Hot) based on urgency.',
        },
        {
          title: 'Kanban vs List View',
          content: 'Toggle between Kanban (visual pipeline) and List (detailed table) views. Drag-and-drop leads between stages in Kanban view. Use List view for bulk operations and detailed filtering.',
        },
        {
          title: 'Updating Lead Information',
          content: 'Click on a lead card to view details. Edit information, add notes, update stage, and track activities. All changes are logged in the stage history.',
        },
        {
          title: 'Lead Assignment',
          content: 'Leads can be auto-assigned by the system or manually assigned by admins. View assigned leads in the "Assigned To" field. Sales users see only their assigned leads.',
        },
        {
          title: 'Adding Payments',
          content: 'Track partial and full payments. Any payment automatically moves the lead to appropriate payment stage. Full payment converts status to "WON" and enables client conversion.',
        },
        {
          title: 'Converting to Client',
          content: 'Once payment is received, convert the lead to a client. This creates a client record and moves them to the service delivery pipeline. Lead data transfers automatically.',
        }
      ]
    },
    {
      id: 'client-management',
      category: 'Client Management',
      color: '#8b5cf6',
      title: 'Managing Clients',
      description: 'Service delivery and client management guide',
      steps: [
        {
          title: 'Client Lifecycle Stages',
          content: 'Clients move through: Welcome Call → Checklist Shared → Documents Received → Processing → Visa Approved/Rejected → Delivered. Each stage represents a milestone in service delivery.',
        },
        {
          title: 'Document Checklist',
          content: 'Create and manage document checklists for each client. Track document status (Pending/Uploaded/Verified/Rejected). Add notes for each document requirement.',
        },
        {
          title: 'Service Delivery Tracking',
          content: 'Monitor progress through each stage. Update status as you complete tasks. Use notes to document important communications and decisions.',
        },
        {
          title: 'Client Communication',
          content: 'All communication history is tracked. Schedule follow-ups, log calls and emails. Use the activity timeline to view complete interaction history.',
        },
        {
          title: 'Completion & Delivery',
          content: 'Once visa is approved/rejected, move to "Delivered" stage. This marks service completion and archives the client record for reporting.',
        }
      ]
    },
    {
      id: 'agreements',
      category: 'Agreements',
      color: '#f59e0b',
      title: 'Agreement Management',
      description: 'Creating and managing service agreements',
      steps: [
        {
          title: 'Creating Agreements',
          content: 'Click "+ Create Agreement". Select the client/lead, set service details, amount, and terms. The system auto-generates a unique agreement number (AGR-YYYYMMDD-XXXX).',
        },
        {
          title: 'QR Code Generation',
          content: 'Each agreement gets a unique QR code containing agreement details. This QR code can be scanned for quick verification and tracking.',
        },
        {
          title: 'Agreement Status',
          content: 'Agreements have 4 statuses: Draft → Sent to Client → Signed → Cancelled. Track the status to know where each agreement stands.',
        },
        {
          title: 'Sending to Clients',
          content: 'Use "Send to Client" button to email the agreement. This updates the status and notifies the lead. The lead\'s stage also updates automatically.',
        },
        {
          title: 'E-Signature Integration',
          content: 'When client signs the agreement, update status to "Signed". This triggers lead stage update to "Agreement Signed" and prepares for payment tracking.',
        },
        {
          title: 'Agreement Reports',
          content: 'View statistics including total agreements, signed count, and conversion rates. Filter by status and date range for detailed analysis.',
        }
      ]
    },
    {
      id: 'lead-scoring',
      category: 'Smart Lead Management',
      color: '#ef4444',
      title: 'Lead Scoring System',
      description: 'Prioritize high-quality leads automatically',
      steps: [
        {
          title: 'What is Lead Scoring?',
          content: 'Lead scoring assigns points to leads based on quality indicators. Higher scores mean higher priority. Scores range from 0-100 by default.',
        },
        {
          title: 'Creating Scoring Rules',
          content: 'Go to Lead Scoring page. Click "Create Scoring Rule". Define parameters like Source Quality, Response Speed, Budget Indicated, etc. Assign points to each parameter.',
        },
        {
          title: 'Scoring Categories',
          content: '7 categories available: Source Quality (website=high), Service Type (visa type value), Response Speed (quick response=high), Budget Indicated (has budget=high), Contact Completeness, Engagement Level, and Custom parameters.',
        },
        {
          title: 'Parameter Points',
          content: 'Assign 1-100 points per parameter. Total of all parameters determines max possible score. Example: Website submission (20pts) + Quick response (15pts) + Complete contact (10pts) = 45 points.',
        },
        {
          title: 'Activating Rules',
          content: 'Only one scoring rule can be active at a time. Click "Activate" on your preferred rule. New leads are automatically scored using the active rule.',
        },
        {
          title: 'Viewing Lead Scores',
          content: 'Lead scores appear on lead cards in Kanban view. Higher scores are highlighted. Use score filters to focus on high-priority leads.',
        },
        {
          title: 'Updating Scores',
          content: 'Scores recalculate when lead data changes. Edit scoring rules anytime - existing leads are re-scored automatically based on new rules.',
        }
      ]
    }
  ];

  const toggleGuide = (guideId) => {
    const newExpanded = new Set(expandedGuides);
    if (newExpanded.has(guideId)) {
      newExpanded.delete(guideId);
    } else {
      newExpanded.add(guideId);
    }
    setExpandedGuides(newExpanded);
  };

  // Filter guides by search query and category
  const filteredGuides = guides.filter(guide => {
    const matchesSearch = 
      guide.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      guide.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      guide.category.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || guide.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const categories = [...new Set(guides.map(g => g.category))];

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '32px',
        padding: '20px 0',
        borderBottom: '2px solid #e5e7eb'
      }}>
        <div>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: '700', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            color: '#1f2937',
            marginBottom: '8px'
          }}>
            <HelpCircle size={32} style={{ color: '#8b5cf6' }} /> 
            Help Center
          </h1>
          <p style={{ color: '#6b7280', fontSize: '15px' }}>Comprehensive guides to help you master the CRM</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '12px',
          alignItems: 'center'
        }}>
          <div style={{ position: 'relative' }}>
            <Search size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              type="text"
              placeholder="Search for help articles, guides, or features..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 12px 12px 40px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>
          <select 
            value={selectedCategory} 
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{
              padding: '12px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Categories ({guides.length})</option>
            {categories.map((cat, idx) => (
              <option key={idx} value={cat}>
                {cat} ({guides.filter(g => g.category === cat).length})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Guides List */}
      <div>
        {filteredGuides.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            background: 'white',
            borderRadius: '12px',
            border: '2px dashed #e5e7eb'
          }}>
            <Search size={48} style={{ color: '#d1d5db', marginBottom: '16px' }} />
            <p style={{ color: '#6b7280', fontSize: '16px' }}>No guides match your search</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {filteredGuides.map(guide => {
              const isExpanded = expandedGuides.has(guide.id);
              return (
                <div 
                  key={guide.id} 
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    border: '2px solid #e5e7eb',
                    overflow: 'hidden',
                    transition: 'all 0.2s',
                    boxShadow: isExpanded ? '0 8px 16px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                >
                  {/* Guide Header */}
                  <div style={{ 
                    padding: '20px',
                    borderBottom: isExpanded ? '2px solid #f3f4f6' : 'none'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      marginBottom: '12px'
                    }}>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: `${guide.color}20`,
                          color: guide.color
                        }}>
                          {guide.category}
                        </span>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: '#f3f4f6',
                          color: '#6b7280'
                        }}>
                          <FileText size={12} style={{ display: 'inline', marginRight: '4px' }} />
                          {guide.steps.length} steps
                        </span>
                      </div>
                    </div>

                    {/* Title */}
                    <div 
                      onClick={() => toggleGuide(guide.id)}
                      style={{ 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px'
                      }}
                    >
                      <BookOpen size={20} style={{ color: guide.color, flexShrink: 0, marginTop: '2px' }} />
                      <div style={{ flex: 1 }}>
                        <h4 style={{ 
                          fontSize: '17px', 
                          fontWeight: '600',
                          color: '#1f2937',
                          margin: 0,
                          lineHeight: '1.5',
                          marginBottom: '4px'
                        }}>
                          {guide.title}
                        </h4>
                        <p style={{ 
                          fontSize: '14px',
                          color: '#6b7280',
                          margin: 0,
                          lineHeight: '1.6'
                        }}>
                          {guide.description}
                        </p>
                      </div>
                      <div style={{ flexShrink: 0 }}>
                        {isExpanded ? <ChevronUp size={20} color="#6b7280" /> : <ChevronDown size={20} color="#6b7280" />}
                      </div>
                    </div>
                  </div>

                  {/* Steps - Expandable */}
                  {isExpanded && (
                    <div style={{ padding: '20px', background: '#f9fafb' }}>
                      {guide.steps.map((step, index) => (
                        <div 
                          key={index}
                          style={{
                            background: 'white',
                            padding: '16px',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                            marginBottom: index === guide.steps.length - 1 ? 0 : '12px',
                            display: 'flex',
                            gap: '16px'
                          }}
                        >
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: guide.color,
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            fontWeight: '700',
                            flexShrink: 0
                          }}>
                            {index + 1}
                          </div>
                          <div style={{ flex: 1 }}>
                            <h5 style={{ 
                              fontSize: '15px',
                              fontWeight: '600',
                              color: '#1f2937',
                              margin: '0 0 8px 0'
                            }}>
                              {step.title}
                            </h5>
                            <p style={{ 
                              fontSize: '14px',
                              lineHeight: '1.7',
                              color: '#6b7280',
                              margin: 0
                            }}>
                              {step.content}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpCenter;
